import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    email,
    regex,
    minLength,
    useTranslate,
    Toolbar,
    SaveButton,
    useRedirect,
    useNotify,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, IconButton } from '@mui/material';
import { Business, ContactPhone, Settings, ArrowBack, Info } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';
import { ApiAccessSection } from './ApiAccessSection';

const VENDOR_KNOWN_FIELDS = new Set([
    'name', 'code', 'vendorType', 'contactEmail', 'contactPhone',
    'billingInfo', 'settings', 'isActive', 'allowedSites',
    'apiAllowedOperations', 'apiControlLimitPerMinute', 'apiReadLimitPerMinute',
]);

const mapVendorBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const vendorFieldLabelKey = (field: string): string =>
    VENDOR_KNOWN_FIELDS.has(field)
        ? `resources.vendors.fields.${field}`
        : field;

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/vendors')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.vendors.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.vendors.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const VendorsCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});
    const validateName = [required(translate('ra.validation.required'))];
    const validateCode = [
        required(translate('ra.validation.required')),
        minLength(3, translate('ra.validation.minLength', { min: 3 })),
        regex(/^[A-Z0-9_]+$/, translate('resources.vendors.validation.codeFormat') ||
              'Must contain only uppercase letters, numbers, and underscores'),
    ];
    const validateEmail = [email(translate('ra.validation.email'))];

    return (
        <Create
            title={false}
            component="div"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.vendors.notifications.created', { type: 'success', messageArgs: { _: 'Vendor created' } });
                    redirect('list', 'vendors');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapVendorBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(vendorFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            {translate('resources.vendors.create.infoMessage') ||
                             'Vendors are external organizations that manage RFID cards and users for their employees or clients.'}
                        </Typography>
                    </Alert>

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Business sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.basicInfo') || 'Basic Information'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    label={translate('resources.vendors.fields.name')}
                                    validate={validateName}
                                    fullWidth
                                    helperText={translate('resources.vendors.create.nameHelperText') ||
                                              'The official name of the vendor organization'}
                                />

                                <TextInput
                                    source="code"
                                    label={translate('resources.vendors.fields.code')}
                                    validate={validateCode}
                                    fullWidth
                                    helperText={translate('resources.vendors.create.codeHelperText') ||
                                              'Unique identifier for API integration (e.g., VENDOR_ABC)'}
                                />

                                <SelectInput
                                    source="vendorType"
                                    label={translate('resources.vendors.fields.vendorType') || 'Vendor Type'}
                                    choices={[
                                        { id: 'building_owner', name: translate('resources.vendors.types.building_owner') || 'Building Owner' },
                                        { id: 'facility_manager', name: translate('resources.vendors.types.facility_manager') || 'Facility Manager' },
                                        { id: 'partner', name: translate('resources.vendors.types.partner') || 'Partner' },
                                        { id: 'corporate', name: translate('resources.vendors.types.corporate') || 'Corporate' },
                                    ]}
                                    validate={required()}
                                    fullWidth
                                    helperText={translate('resources.vendors.create.vendorTypeHelperText') ||
                                              'Select the type of vendor organization'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Contact Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <ContactPhone sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.contactInfo') || 'Contact Information'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="contactEmail"
                                    label={translate('resources.vendors.fields.contactEmail')}
                                    validate={validateEmail}
                                    fullWidth
                                    helperText={translate('resources.vendors.create.contactEmailHelperText') ||
                                              'Email address for vendor communications'}
                                />

                                <TextInput
                                    source="contactPhone"
                                    label={translate('resources.vendors.fields.contactPhone')}
                                    fullWidth
                                    helperText={translate('resources.vendors.create.contactPhoneHelperText') ||
                                              'Phone number for urgent matters'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    <ApiAccessSection />

                    {/* Settings Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.settings') || 'Settings'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.vendors.fields.isActive')}
                                    defaultValue={true}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Info sx={{ fontSize: 20, mr: 1, color: '#1976D2' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {translate('resources.vendors.create.siteAssignmentInfo') || 'Site Assignment'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.vendors.create.siteAssignmentHelperText') ||
                                         'After creating the vendor, you can assign sites they can manage through the vendor edit page.'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mt: 1, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Info sx={{ fontSize: 20, mr: 1, color: '#1976D2' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {translate('resources.vendors.create.billingInfo') || 'Billing Configuration'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.vendors.create.billingHelperText') ||
                                         'Billing information and settings can be configured after creation if needed for cost allocation.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};